import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import { errorAlertMessageFrom } from "../../error";
import { usePricingSummary } from "./use-pricing-summary";
import { MATTER_TEMPLATE_QUERY } from "../../graphql/query/matter-template-query";
import { MatterTemplateQuery, MatterTemplateQueryVariables } from "~/gql/graphql";
import { useOrganisationalUnitsQuery } from "../../graphql/query";
import { useUpdateMatterTemplateMutation } from "./use-update-matter-template-mutation";
import {
  MatterUpdateInput,
  PricingLevel,
  PricingType,
} from "../../graphql/__generated_types__/globalTypes";
import { AlertMessage } from "@design-system";
import { MatterTemplate, PricingOptions, PriceRange } from "@design-system";

export interface MatterTemplateEditorViewModel {
  loading(): boolean;
  alertMessage(): AlertMessage | undefined;
  practiceAreas(): { id: number; name: string }[] | undefined;
  matterTemplate(): MatterTemplate | undefined;
  pricingOptions(): PricingOptions;
  setPracticeArea(practiceAreaId: number): void;
  setTitle(title: string): void;
  setPriceOptions(pricingType: PricingType, value: PriceRange | number | null): void;
  setPricingStructure(pricingType: PricingType, pricingLevel: PricingLevel): void;
}

export function useMatterTemplateEditorViewModel(
  matterTemplateId: number
): MatterTemplateEditorViewModel {
  const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
  const { loading, data } = useQuery<MatterTemplateQuery, MatterTemplateQueryVariables>(
    MATTER_TEMPLATE_QUERY,
    {
      variables: { id: matterTemplateId },
      onError: (error) =>
        setAlertMessage(errorAlertMessageFrom(error, "Failed to load matter template")),
    }
  );
  const orgUnitsQuery = useOrganisationalUnitsQuery({
    onError: (error) =>
      setAlertMessage(errorAlertMessageFrom(error, "Failed to load practice areas")),
  });
  const pricingOptions = usePricingSummary(data?.matterTemplate);
  const [updateMatterTemplate] = useUpdateMatterTemplateMutation({
    onError: (error) =>
      setAlertMessage(errorAlertMessageFrom(error, "Failed to update matter template")),
  });

  return useMemo(
    () => ({
      loading: () => loading || orgUnitsQuery?.loading === true,
      alertMessage: () => alertMessage,
      practiceAreas: () =>
        orgUnitsQuery?.data ? orgUnitsQuery.data.organisationalUnits : undefined,
      matterTemplate: (): MatterTemplate | undefined =>
        data?.matterTemplate as MatterTemplate | undefined,
      pricingOptions: (): PricingOptions => pricingOptions,
      setPracticeArea: (organisationalUnitId) =>
        updateMatterTemplate(
          { organisationalUnit: { connect: { id: organisationalUnitId } } },
          matterTemplateId
        ),
      setTitle: (title: string) =>
        updateMatterTemplate({ title: { set: title } }, matterTemplateId),
      setPriceOptions: (pricingType, value) =>
        updateMatterTemplate({ ...newPriceOptionsUpdate(pricingType, value) }, matterTemplateId),
      setPricingStructure: (pricingType, pricingLevel) =>
        updateMatterTemplate(
          newPricingStructureUpdate(pricingType, pricingLevel),
          matterTemplateId
        ),
    }),
    [
      matterTemplateId,
      loading,
      alertMessage,
      data,
      orgUnitsQuery,
      pricingOptions,
      updateMatterTemplate,
    ]
  );
}

const newPriceOptionsUpdate = (
  pricingType: PricingType,
  value: PriceRange | number | null
): MatterUpdateInput => ({
  priceOptions: {
    [pricingType]: {
      chosen: value,
    },
  },
});

const newPricingStructureUpdate = (
  pricingType: PricingType,
  pricingLevel: PricingLevel
): MatterUpdateInput => ({
  pricingType: { set: pricingType },
  pricingLevel: { set: pricingLevel },
});
